import { useState } from "react"
import { BarController, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineController, LineElement, PointElement, Tooltip } from "chart.js"
import { Chart } from "react-chartjs-2"
import { ArrowDown, ArrowUp } from "lucide-react"

import { cn } from "@/lib/utils"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

ChartJS.register(BarController, LineController, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend)

// P&L "plan vs actual" section. Every number here is on one basis: the months that have
// both a plan and an actual (the API's `varianceForecast` / `varianceActual` totals), so the
// scorecard, the bridge and the variance always agree with each other.

type Figure = { revenue: string; totalCost: string; profit: string }
type Row = { year: number; month: number; comparable: boolean; actual: Figure; forecast: Figure }

const GOOD = "#1e7a4c"
const BAD = "#c2412d"
const PLAN = "#2563eb"
const NEUTRAL = "#64748b"
const MONTHS = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"]
const PERSIAN = "۰۱۲۳۴۵۶۷۸۹"
const yy = (year: number) => String(year % 100).padStart(2, "0").replace(/\d/g, (digit) => PERSIAN[Number(digit)]!)
const monthShort = (row: { year: number; month: number }) => `${MONTHS[row.month - 1]} ${yy(row.year)}`
const millions = (rial: string) => Number(rial) / 1_000_000
const fmt = (value: number) => value.toLocaleString("fa-IR", { maximumFractionDigits: 0 })
const signed = (value: number) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${fmt(Math.abs(value))}`
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches
const hasActual = (row: Row) => row.actual.revenue !== "0" || row.actual.totalCost !== "0"

/** «مهر ۰۴ تا شهریور ۰۵ — ۱۲ ماه» for the months both plan and actual exist. */
export function comparablePeriod(rows: Row[]) {
  const months = rows.filter((row) => row.comparable)
  if (!months.length) return null
  const first = months[0]!; const last = months.at(-1)!
  return `${monthShort(first)}${months.length > 1 ? ` تا ${monthShort(last)}` : ""} — ${months.length.toLocaleString("fa-IR")} ماه`
}

// ─── A. scorecard ───────────────────────────────────────────────────────────

export function PnlScorecard({ plan, actual, period }: { plan: Figure; actual: Figure; period: string | null }) {
  const items = [
    { title: "درآمد", plan: millions(plan.revenue), actual: millions(actual.revenue), higherIsBetter: true },
    { title: "هزینه", plan: millions(plan.totalCost), actual: millions(actual.totalCost), higherIsBetter: false },
    { title: "سود / زیان", plan: millions(plan.profit), actual: millions(actual.profit), higherIsBetter: true, strong: true },
  ]
  return (
    <section aria-labelledby="scorecard-title" className="grid gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="scorecard-title" className="text-base font-semibold">عملکرد در برابر برنامه</h2>
        <p className="text-sm text-muted-foreground">{period ? `ماه‌های دارای برنامه و عملکرد: ${period}` : "هنوز ماهی با برنامه و عملکرد هم‌زمان وجود ندارد."}؛ ارقام به میلیون ریال</p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {items.map((item) => <ScoreCard key={item.title} {...item} />)}
      </div>
    </section>
  )
}

function ScoreCard({ title, plan, actual, higherIsBetter, strong }: { title: string; plan: number; actual: number; higherIsBetter: boolean; strong?: boolean }) {
  const gap = actual - plan
  const favourable = higherIsBetter ? gap >= 0 : gap <= 0
  // "% of plan" only reads sensibly when both are positive.
  const ratio = plan > 0 && actual >= 0 ? actual / plan : null
  // Bullet bar on a scale that always contains zero, the actual and the plan.
  const low = Math.min(0, actual, plan); const high = Math.max(0, actual, plan) || 1
  const span = high - low || 1
  const pos = (value: number) => ((value - low) / span) * 100
  const zero = pos(0)
  return (
    <Card className={cn(strong && "border-2", strong && (actual < 0 ? "border-loss/40" : "border-success/40"))}>
      <CardHeader className="pb-2">
        <CardDescription>{title} — محقق</CardDescription>
        <CardTitle className={cn("text-3xl tabular-nums", actual < 0 && "text-loss")}><span dir="ltr">{signed(actual).replace(/^\+/, "")}</span></CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        <div className="relative h-3 rounded-full bg-muted" role="img" aria-label={`${title}: محقق ${fmt(actual)}، برنامه ${fmt(plan)} میلیون ریال`}>
          <div className="absolute top-0 bottom-0 rounded-full" style={{ insetInlineStart: `${Math.min(zero, pos(actual))}%`, width: `${Math.abs(pos(actual) - zero)}%`, background: actual < 0 ? BAD : strong ? GOOD : NEUTRAL }} />
          <div className="absolute -top-1 -bottom-1 w-0.5 rounded bg-[#2563eb]" style={{ insetInlineStart: `${pos(plan)}%` }} title="برنامه" />
          {low < 0 && <div className="absolute -top-0.5 -bottom-0.5 w-px bg-foreground/40" style={{ insetInlineStart: `${zero}%` }} />}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground"><span className="me-1 inline-block h-3 w-0.5 translate-y-0.5 bg-[#2563eb]" aria-hidden />برنامه: <span className="tabular-nums" dir="ltr">{signed(plan).replace(/^\+/, "")}</span></span>
          <span className={cn("inline-flex items-center gap-1 font-medium tabular-nums", favourable ? "text-success" : "text-loss")}>
            {gap >= 0 ? <ArrowUp className="size-3.5" aria-hidden /> : <ArrowDown className="size-3.5" aria-hidden />}
            <span dir="ltr">{signed(gap)}</span>
            <span className="sr-only">{favourable ? "(مطلوب)" : "(نامطلوب)"}</span>
          </span>
        </div>
        {ratio !== null && <p className="text-xs text-muted-foreground">{(ratio * 100).toLocaleString("fa-IR", { maximumFractionDigits: 0 })}٪ برنامه</p>}
      </CardContent>
    </Card>
  )
}

// ─── B. profit bridge ───────────────────────────────────────────────────────

/** Planned profit → revenue effect → cost effect → actual profit (floating bars). */
export function ProfitBridge({ plan, actual }: { plan: Figure; actual: Figure }) {
  const planned = millions(plan.profit)
  const revenueEffect = millions(actual.revenue) - millions(plan.revenue)
  const costEffect = millions(plan.totalCost) - millions(actual.totalCost)
  const result = millions(actual.profit)
  const steps = [
    { label: "سود برنامه", range: [0, planned] as [number, number], color: PLAN, value: planned },
    { label: "اثر درآمد", range: [planned, planned + revenueEffect] as [number, number], color: revenueEffect >= 0 ? GOOD : BAD, value: revenueEffect, delta: true },
    { label: "اثر هزینه", range: [planned + revenueEffect, planned + revenueEffect + costEffect] as [number, number], color: costEffect >= 0 ? GOOD : BAD, value: costEffect, delta: true },
    { label: "سود محقق", range: [0, result] as [number, number], color: result < 0 ? BAD : NEUTRAL, value: result },
  ]
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">پل سود: چرا سود محقق با برنامه فرق دارد؟</CardTitle>
        <CardDescription>از سود برنامه، با اثر کمبود/مازاد درآمد و صرفه‌جویی/افزایش هزینه، به سود محقق می‌رسیم (میلیون ریال).</CardDescription>
      </CardHeader>
      <CardContent className="grid items-center gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="h-72">
          <Chart
            type="bar"
            data={{ labels: steps.map((step) => step.label), datasets: [{ data: steps.map((step) => step.range), backgroundColor: steps.map((step) => step.color), borderRadius: 4, barPercentage: 0.6 }] }}
            options={{
              responsive: true, maintainAspectRatio: false,
              animation: { duration: reducedMotion() ? 0 : 400 },
              plugins: {
                legend: { display: false },
                tooltip: { rtl: true, textDirection: "rtl", callbacks: { label: (context) => { const step = steps[context.dataIndex]!; return `${step.label}: ${step.delta ? signed(step.value) : fmt(step.value)} میلیون ریال` } } },
              },
              scales: {
                x: { reverse: true, grid: { display: false } },
                y: { grid: { color: (context) => (context.tick.value === 0 ? "rgba(15,23,42,0.5)" : "rgba(148,163,184,0.25)") }, ticks: { callback: (value) => Number(value).toLocaleString("fa-IR") } },
              },
            }}
            aria-label={`پل سود: سود برنامه ${fmt(planned)}، اثر درآمد ${signed(revenueEffect)}، اثر هزینه ${signed(costEffect)}، سود محقق ${fmt(result)} میلیون ریال`}
            role="img"
          />
        </div>
        <dl className="grid gap-2 text-sm">
          {steps.map((step) => (
            <div key={step.label} className={cn("flex items-center justify-between gap-3 rounded-md px-2 py-1.5", !step.delta && "bg-muted/60 font-medium")}>
              <dt className="flex items-center gap-2"><span aria-hidden className="size-2.5 rounded-sm" style={{ background: step.color }} />{step.label}</dt>
              <dd dir="ltr" className={cn("tabular-nums", step.value < 0 && "text-loss", step.delta && step.value > 0 && "text-success")}>{step.delta ? signed(step.value) : signed(step.value).replace(/^\+/, "")}</dd>
            </div>
          ))}
          <p className="pt-1 text-xs text-muted-foreground">اثر هزینه مثبت یعنی هزینه کمتر از برنامه بوده است.</p>
        </dl>
      </CardContent>
    </Card>
  )
}

// ─── C. monthly profit ──────────────────────────────────────────────────────

/** Actual profit bars (green/red) against the planned profit line; monthly or cumulative. */
export function MonthlyProfitChart({ rows }: { rows: Row[] }) {
  const [mode, setMode] = useState<"monthly" | "cumulative">("monthly")
  const actualRaw = rows.map((row) => (hasActual(row) ? millions(row.actual.profit) : null))
  const planRaw = rows.map((row) => millions(row.forecast.profit))
  const cumulate = (values: Array<number | null>) => { let sum = 0; let open = true; return values.map((value) => { if (value === null) { open = false; return null } if (!open) return null; sum += value; return sum }) }
  const actualValues = mode === "monthly" ? actualRaw : cumulate(actualRaw)
  const planValues = mode === "monthly" ? planRaw : cumulate(planRaw)
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">سود ماه به ماه</CardTitle>
          <CardDescription>ستون‌ها سود محقق هر ماه (سبز سود، قرمز زیان) و خط‌چین آبی سود برنامه است (میلیون ریال).</CardDescription>
        </div>
        <div className="inline-flex rounded-lg border bg-muted p-1" role="radiogroup" aria-label="نمایش">
          {([["monthly", "ماهانه"], ["cumulative", "تجمعی"]] as const).map(([value, label]) => (
            <button key={value} type="button" role="radio" aria-checked={mode === value} onClick={() => setMode(value)}
              className={cn("rounded-md px-3 py-1 text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", mode === value ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>{label}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <Chart
            type="bar"
            data={{
              labels: rows.map(monthShort),
              datasets: [
                { type: "bar" as const, label: mode === "monthly" ? "سود محقق" : "سود محقق تجمعی", data: actualValues, backgroundColor: actualValues.map((value) => ((value ?? 0) < 0 ? BAD : GOOD)), borderRadius: 3, order: 2 },
                { type: "line" as const, label: mode === "monthly" ? "سود برنامه" : "سود برنامه تجمعی", data: planValues, borderColor: PLAN, backgroundColor: PLAN, borderDash: [6, 5], borderWidth: 2, pointRadius: 3, tension: 0.25, order: 1 },
              ],
            }}
            options={{
              responsive: true, maintainAspectRatio: false,
              animation: { duration: reducedMotion() ? 0 : 400 },
              interaction: { mode: "index", intersect: false },
              plugins: {
                // Bars are coloured per month (profit/loss); the legend swatch must show the profit colour, not month one's.
                legend: { position: "bottom", rtl: true, textDirection: "rtl", labels: { usePointStyle: true, padding: 16, generateLabels: (chart) => ChartJS.defaults.plugins.legend.labels.generateLabels(chart).map((item) => (item.datasetIndex === 0 ? { ...item, fillStyle: GOOD, strokeStyle: GOOD } : item)) } },
                tooltip: { rtl: true, textDirection: "rtl", callbacks: { label: (context) => (context.parsed.y === null ? `${context.dataset.label}: —` : `${context.dataset.label}: ${fmt(Number(context.parsed.y))} میلیون ریال`) } },
              },
              scales: {
                x: { reverse: true, grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true } },
                y: { grid: { color: (context) => (context.tick.value === 0 ? "rgba(15,23,42,0.5)" : "rgba(148,163,184,0.25)") }, ticks: { callback: (value) => Number(value).toLocaleString("fa-IR"), maxTicksLimit: 7 } },
              },
            }}
            aria-label="سود محقق و برنامه هر ماه"
            role="img"
          />
        </div>
      </CardContent>
    </Card>
  )
}
